/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useState } from "react";
import "./MembershipPaymentDetailScreen.css";
import backIcon from "../../../assets/images/back-icon.svg";
import paymentRightArrow from "../../../assets/images/membership-payment-right-arrow.svg";
import letsDoItIcon from "../../../assets/images/lets-do-it-icon.svg";
import paymentCreditIcon from "../../../assets/images/payment-mode-credit-icon.svg";
import paymentStarIcon from "../../../assets/images/communicate-star-icon.svg";
import offerAppliedIcon from "../../../assets/images/membership-offer-applied-icon.svg";
import { useNavigate } from "react-router-dom";
import { getMembershipCardImage } from "utils/utils";
import { useMutation } from "@tanstack/react-query";
import useWSMApi from "services/api/useWSMApi";
import SnackbarAlert from "components/elements/SnackbarAlert";
import { CircularProgress } from "@mui/material";
import {
  MEMBERSHIP_PLAN_TYPE,
  PAYMENT_STATUS,
  PROFILE_STATUS,
  PURCHASE_TYPE,
} from "utils/constants";
import { MESSAGE, STORAGE_KEY } from "utils/strings";
import ROUTE_PATHS from "utils/routePaths";
import { useStorage } from "services/StorageProvider";
import {
  trackCheckoutStarted,
  trackPaymentInitiated,
  trackPaymentSuccess,
  trackPaymentFailed,
  trackCheckoutAbandoned,
  trackBackClicked,
} from "utils/analytics";

const MembershipPaymentDetailScreen: FC = () => {
  const navigate = useNavigate();
  const { userProfileData } = useStorage();
  const [selectedMembershipData, setSelectedMembershipData] =
    useState<any>(null);
  const [selectedPlanData, setSelectedPlanData] = useState<any>(null);
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [paymentId, setPaymentId] = useState<string>("");
  const { createPayment, verifyPayment } = useWSMApi();

  const purchaseMembershipMutation = useMutation({
    mutationFn: (data: any) => createPayment(data),
    onSuccess: (response, obj) => {
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: obj.product.total_price,
        currency: obj.currency,
        name: "The Foundry", //Name Shown in Razorpay Form
        description: obj.product.description,
        order_id: response.id, // This is the order_id created in the backend
        prefill: {
          name: obj.customer.name,
          email: obj.customer.email,
          contact: obj.customer.contact,
        },
        theme: {
          color: "#1C1C1E",
        },
        handler: async function (paymentResponse: any) {
          setPaymentId(paymentResponse.razorpay_payment_id);
          trackPaymentSuccess(
            obj.product.total_price / 100,
            paymentResponse.razorpay_payment_id,
            obj.product.description,
            "membership",
          );
          verifyPaymentMutation.mutate({
            razorpay_order_id: paymentResponse.razorpay_order_id,
            razorpay_payment_id: paymentResponse.razorpay_payment_id,
            razorpay_signature: paymentResponse.razorpay_signature,
          });
        },
        modal: {
          ondismiss: () => {
            if (!paymentStatus) {
              setPaymentStatus(PAYMENT_STATUS.CANCELLED);
              trackCheckoutAbandoned("payment");
            }
          },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    },
  });

  const verifyPaymentMutation = useMutation({
    mutationFn: (data: any) => verifyPayment(data),
    onSuccess: (response) => {
      setPaymentStatus(response.status);
    },
    onError: () => {
      setPaymentStatus(PAYMENT_STATUS.FAILED);
      trackPaymentFailed("payment_verification_failed");
    },
  });

  useEffect(() => {
    const storedMembershipData = localStorage.getItem(
      STORAGE_KEY.SELECTED_MEMBERSHIP_DATA,
    );
    if (storedMembershipData) {
      setSelectedMembershipData(JSON.parse(storedMembershipData));
    }
    const storedPlanData = localStorage.getItem(
      STORAGE_KEY.SELECTED_MEMBERSHIP_PLAN_DATA,
    );
    if (storedPlanData) {
      const plan = JSON.parse(storedPlanData);
      setSelectedPlanData(plan);
      // ← ADD: fire begin_checkout when screen mounts with plan data
      const price = plan?.discounted_price ?? plan?.plan_price ?? 0;
      trackCheckoutStarted(Number(price));
    }
  }, []);

  useEffect(() => {
    if (paymentStatus === PAYMENT_STATUS.SUCCESS) {
      const paymentData = {
        title: `${selectedMembershipData?.membership_type} Membership`,
        type: PURCHASE_TYPE.MEMBERSHIP,
      };
      localStorage.setItem(
        STORAGE_KEY.PAYMENT_SUCCESS_DATA,
        JSON.stringify(paymentData),
      );
      navigate(ROUTE_PATHS.WSM_ALL_SET, { replace: true });
    }
  }, [paymentStatus, paymentId, navigate]);

  const handleBackButtonClick = () => {
    trackBackClicked("MembershipPayment");
    navigate(-1);
  };

  const handleProceedToPaymentClick = () => {
    const planPrice = Number(getFinalPlanPrice());
    const amountInSubunits = Math.round(planPrice * 100);
    const passedObj = {
      amount: planPrice,
      currency: "INR",
      customer: {
        name: `${userProfileData?.first_name} ${userProfileData?.last_name}`,
        email: userProfileData?.email,
        contact: userProfileData?.mobile_number,
      },
      product: {
        total_price: amountInSubunits, // Amount in paise
        membership_id: selectedMembershipData?.id,
        membership_plan_id: selectedPlanData?.id,
        description: `${selectedMembershipData?.membership_type} Membership`,
        price: selectedPlanData?.plan_price,
        discounted_price:
          selectedPlanData?.plan_price - selectedPlanData?.discounted_price,
        sub_total_price: getPlanPrice(),
        with_gst_price: getPlanPriceWithGST(),
        credit: selectedPlanData?.total_credits,
        validity: Number(
          selectedPlanData?.validity + selectedPlanData?.complimentary_validity,
        ),
      },
    };
    setPaymentStatus(null);
    trackPaymentInitiated("razorpay");
    purchaseMembershipMutation.mutate(passedObj);
  };

  const getPerDayPrice = () => {
    const planPrice = selectedPlanData?.discounted_price
      ? selectedPlanData?.discounted_price
      : selectedPlanData?.plan_price;
    const planDays =
      (selectedPlanData?.validity + selectedPlanData?.complimentary_validity) *
      30;
    return Math.floor(planPrice / planDays);
  };

  const getPlanPrice = () => {
    return selectedPlanData?.discounted_price
      ? selectedPlanData?.discounted_price
      : selectedPlanData?.plan_price;
  };

  const getPlanPriceWithGST = () => {
    const price = getPlanPrice();
    return Math.floor((Number(price) * selectedPlanData?.plan_gst) / 100);
  };

  const getFinalPlanPrice = () => {
    const price = getPlanPrice();
    const gstPrice = (Number(price) * selectedPlanData?.plan_gst) / 100;
    return Math.floor(Number(price) + gstPrice);
  };

  return (
    <div className="wms-mobile-container">
      {purchaseMembershipMutation?.isError ? (
        <SnackbarAlert type={"error"}>
          {purchaseMembershipMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {paymentStatus === PAYMENT_STATUS.FAILED ? (
        <SnackbarAlert type={"error"}>{MESSAGE.PAYMENT_FAILED}</SnackbarAlert>
      ) : (
        ""
      )}
      <div className="membership-payment-detail-screen-container">
        <div className="membership-payment-detail-header-container">
          <img
            src={backIcon}
            alt="dropdown-logo"
            className="image"
            onClick={handleBackButtonClick}
          />
          <div className="title">Payment Mode</div>
        </div>
        <div className="membership-payment-detail-tab-bar-background-container">
          <div className="membership-payment-detail-tab-bar-container">
            <div
              className="membership-payment-detail-tab-bar-item-container"
              style={{ fontWeight: "700" }}
            >
              <div className="circle-container review">1</div>
              Review
            </div>
            <img src={paymentRightArrow} alt="payment-right-arrow" />
            <div className="membership-payment-detail-tab-bar-item-container">
              <div className="circle-container payment">2</div>
              Payment
            </div>
          </div>
        </div>

        <div className="membership-payment-detail-card-container">
          <div className="image-text-container">
            <img
              className="card-image"
              src={getMembershipCardImage(
                selectedMembershipData?.membership_type,
              )}
              alt="membership-card"
            />
            <div className="custom-text-overlay"></div>
          </div>
          <div className="right-container">
            <div className="sub-container">
              <div className="plan-amount-container">
                {`INR ${getPlanPrice()?.toLocaleString("en-US")}`}
                <span>Only</span>
              </div>
              {selectedPlanData?.discounted_price && (
                <div className="plan-offer-price-container">
                  {`INR ${selectedPlanData?.plan_price?.toLocaleString(
                    "en-US",
                  )}`}
                </div>
              )}
              <div className="total-credit-text">
                <img src={paymentCreditIcon} alt="credit-icon" />
                {`${selectedPlanData?.total_credits} Credits`}
              </div>
            </div>
            <div className="sub-container">
              <div className="valid-up-to-text">Valid Up to</div>
              <div className="credit-validity-container">
                {`${selectedPlanData?.validity}${
                  selectedPlanData?.complimentary_validity > 0
                    ? ` + ${selectedPlanData?.complimentary_validity} Months`
                    : " Months"
                }`}
              </div>
            </div>
          </div>
        </div>
        <div className="membership-payment-summary-details-container">
          <div className="header-container">
            <span>
              {selectedMembershipData?.membership_type}{" "}
              <span style={{ color: "#C2B58C" }}>membership</span>
            </span>
          </div>
          <div className="divider" style={{ marginBottom: "14px" }}></div>
          <div className="payment-detail">
            <div className="sub-container">
              <img src={paymentStarIcon} alt="star-icon" />
              <span>Price</span>
            </div>
            <span>{`INR ${selectedPlanData?.plan_price?.toLocaleString(
              "en-US",
            )}`}</span>
          </div>
          {selectedPlanData?.discounted_price && (
            <div className="payment-detail">
              <div className="sub-container">
                <img src={paymentStarIcon} alt="star-icon" />
                <span className="discount">Discount</span>
              </div>
              <span className="discount">{`INR ${(
                selectedPlanData?.plan_price -
                selectedPlanData?.discounted_price
              )?.toLocaleString("en-US")}`}</span>
            </div>
          )}
          <div
            className="divider"
            style={{
              marginBottom: "15px",
              backgroundColor: "#595961",
              marginTop: "5px",
            }}
          ></div>
          <div className="payment-detail">
            <div className="sub-container">
              <img src={paymentStarIcon} alt="star-icon" />
              <span>Sub Total</span>
            </div>
            <span>{`INR ${getPlanPrice()?.toLocaleString("en-US")}`}</span>
          </div>
          <div className="payment-detail">
            <div className="sub-container">
              <img src={paymentStarIcon} alt="star-icon" />
              <span>GST</span>
            </div>
            <span>INR {getPlanPriceWithGST()?.toLocaleString("en-US")}</span>
          </div>
          <div
            className="divider"
            style={{
              marginBottom: "15px",
              backgroundColor: "#595961",
              marginTop: "5px",
            }}
          ></div>
          <div className="payment-detail">
            <span className="total">Total Payable</span>
            <span className="total">{`INR ${getFinalPlanPrice()?.toLocaleString(
              "en-US",
            )}`}</span>
          </div>
        </div>
      </div>

      {userProfileData?.credit?.status === PROFILE_STATUS.PAYMENT_PENDING ||
      userProfileData?.credit?.status === PROFILE_STATUS.MEMBER ? (
        <div className="membership-payment-summary-detail-screen-footer-container">
          {selectedPlanData?.discounted_price && (
            <div className="membership-payment-summary-footer-tooltip-container">
              <div className="offer-container">
                <img src={offerAppliedIcon} alt="dropdown-logo" />
              </div>

              <div className="membership-payment-summary-footer-tooltip-text">
                <span className="first-text">
                  {
                    MEMBERSHIP_PLAN_TYPE.find(
                      (type) => type.value === selectedPlanData?.plan_type,
                    )?.name
                  }{" "}
                </span>
                <span className="second-text">You Just</span>
                <span className="third-text">
                  Saved{" "}
                  {(
                    selectedPlanData?.plan_price -
                    selectedPlanData?.discounted_price
                  )?.toLocaleString("en-US")}{" "}
                  INR
                </span>
              </div>
            </div>
          )}
          <div
            className="button-container"
            onClick={handleProceedToPaymentClick}
          >
            <div className="left-container">
              <div className="text-container">
                <span>
                  {selectedPlanData?.discounted_price
                    ? selectedPlanData?.discounted_price?.toLocaleString(
                        "en-US",
                      )
                    : selectedPlanData?.plan_price?.toLocaleString(
                        "en-US",
                      )}{" "}
                  INR
                </span>
                <span className="sub-title">
                  {getPerDayPrice()?.toLocaleString("en-US")} INR /day
                </span>
              </div>
            </div>
            <div className="divider"></div>
            <div className="right-container">
              <span>Pay Now</span>
              <img src={letsDoItIcon} alt="lets-do-it-logo" />
            </div>
          </div>
        </div>
      ) : (
        <div className="membership-payment-summary-detail-screen-footer-container">
          {selectedPlanData?.discounted_price && (
            <div className="membership-payment-summary-footer-tooltip-container">
              <div className="offer-container">
                <img src={offerAppliedIcon} alt="dropdown-logo" />
              </div>

              <div className="membership-payment-summary-footer-tooltip-text">
                <span className="first-text">
                  {
                    MEMBERSHIP_PLAN_TYPE.find(
                      (type) => type.value === selectedPlanData?.plan_type,
                    )?.name
                  }{" "}
                </span>
                <span className="second-text">You Just</span>
                <span className="third-text">
                  Saved{" "}
                  {(
                    selectedPlanData?.plan_price -
                    selectedPlanData?.discounted_price
                  )?.toLocaleString("en-US")}{" "}
                  INR
                </span>
              </div>
            </div>
          )}
          <div
            className="button-container"
            onClick={handleProceedToPaymentClick}
          >
            {purchaseMembershipMutation?.isPending ? (
              <div className="left-container">
                <CircularProgress size={30} color="inherit" />
              </div>
            ) : (
              <div className="left-container">
                <span>Proceed to payment</span>
                <img src={letsDoItIcon} alt="lets-do-it-logo" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipPaymentDetailScreen;
